import React, { useMemo } from 'react';
import useLoyaltyStore from './useLoyaltyStore';
import RewardFormModal from './RewardFormModal';

const CATEGORY_COLORS = {
  food: 'badge-warning',
  health: 'badge-success',
  lifestyle: 'badge-info',
  beauty: 'badge-danger',
  travel: 'badge-primary',
  entertainment: 'badge-secondary',
};

function categoryBadgeClass(category) {
  if (!category) return 'badge-secondary';
  const name = typeof category === 'string' ? category : category.name || '';
  const key = name.toLowerCase().split(/[\s&]+/)[0];
  return CATEGORY_COLORS[key] || 'badge-info';
}

function partnerInitial(name) {
  if (!name) return '?';
  return name.charAt(0).toUpperCase();
}

function QuotaDisplay({ reward }) {
  if (reward.limitless_quota) return <span className="text-muted">Limitless</span>;
  const used = reward.used_vouchers || 0;
  const max = reward.max_vouchers || 0;
  return <span>{used}/{max}</span>;
}

const RewardsTab = ({ initialPartners, initialCategories }) => {
  const {
    rewards,
    rewardsLoading,
    rewardSearch,
    rewardPartnerFilter,
    setRewardSearch,
    setRewardPartnerFilter,
    openModal,
    closeModal,
    modalOpen,
    modalType,
    partners,
    toggleRewardStatus,
    toggleRewardClaimable,
    deleteReward,
  } = useLoyaltyStore();

  const filterPartners = partners.length > 0 ? partners : (initialPartners || []);

  const filteredRewards = useMemo(() => {
    return rewards.filter((reward) => {
      const matchesSearch = !rewardSearch ||
        (reward.name || '').toLowerCase().includes(rewardSearch.toLowerCase());
      const matchesPartner = !rewardPartnerFilter ||
        String(reward.partner?.id) === String(rewardPartnerFilter);
      return matchesSearch && matchesPartner;
    });
  }, [rewards, rewardSearch, rewardPartnerFilter]);

  function handleDelete(reward) {
    if (window.confirm(`Delete reward "${reward.name}"? This cannot be undone.`)) {
      deleteReward(reward.id);
    }
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-4 flex-wrap" style={{ gap: '12px' }}>
        <div className="form-group mb-0 flex-grow-1" style={{ minWidth: '200px' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search rewards..."
            value={rewardSearch}
            onChange={(e) => setRewardSearch(e.target.value)}
          />
          <i className="form-group__bar" />
        </div>

        <div className="form-group mb-0" style={{ minWidth: '180px' }}>
          <select
            className="form-control"
            value={rewardPartnerFilter}
            onChange={(e) => setRewardPartnerFilter(e.target.value)}
          >
            <option value="">All Partners</option>
            {filterPartners.map((partner) => (
              <option key={partner.id} value={partner.id}>{partner.name}</option>
            ))}
          </select>
          <i className="form-group__bar" />
        </div>

        <button
          type="button"
          className="btn btn-success"
          onClick={() => openModal('reward', 'create')}
        >
          <i className="zmdi zmdi-plus mr-1" />
          Add Reward
        </button>
      </div>

      {rewardsLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-success" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>Preview</th>
                <th>Reward Name</th>
                <th>Partner</th>
                <th>Category</th>
                <th>Points</th>
                <th>Quota</th>
                <th>Status</th>
                <th>Claimable</th>
                <th style={{ width: '80px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRewards.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    No rewards found.
                  </td>
                </tr>
              ) : (
                filteredRewards.map((reward) => (
                  <tr key={reward.id}>
                    <td>
                      {reward.image_url ? (
                        <img
                          src={reward.image_url}
                          alt={reward.name}
                          style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '6px' }}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center bg-light rounded"
                          style={{ width: '60px', height: '60px' }}
                        >
                          <i className="zmdi zmdi-image text-muted" style={{ fontSize: '24px' }} />
                        </div>
                      )}
                    </td>
                    <td className="align-middle">
                      <strong>{reward.name}</strong>
                    </td>
                    <td className="align-middle">
                      {reward.partner?.name ? (
                        <div className="d-flex align-items-center">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle mr-2 text-white"
                            style={{ width: '28px', height: '28px', background: '#41924E', fontSize: '12px', fontWeight: 600, flexShrink: 0 }}
                          >
                            {partnerInitial(reward.partner?.name)}
                          </div>
                          <span>{reward.partner?.name}</span>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="align-middle">
                      {reward.category ? (
                        <span className={'badge ' + categoryBadgeClass(reward.category)}>
                          {reward.category.name}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="align-middle">
                      <i className="zmdi zmdi-star text-warning mr-1" />
                      {reward.points_required || 0}
                    </td>
                    <td className="align-middle">
                      <QuotaDisplay reward={reward} />
                    </td>
                    <td className="align-middle">
                      <div className="toggle-switch" style={{ marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          className="toggle-switch__checkbox"
                          id={'reward-status-' + reward.id}
                          checked={reward.status === 'active'}
                          onChange={() => toggleRewardStatus(reward.id)}
                        />
                        <i className="toggle-switch__helper" />
                      </div>
                    </td>
                    <td className="align-middle">
                      <div className="toggle-switch" style={{ marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          className="toggle-switch__checkbox"
                          id={'reward-claimable-' + reward.id}
                          aria-label={'Toggle claimable for ' + reward.name}
                          checked={reward.claimable !== false}
                          onChange={() => toggleRewardClaimable(reward.id)}
                        />
                        <i className="toggle-switch__helper" />
                      </div>
                    </td>
                    <td className="align-middle">
                      <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          title="Edit"
                          onClick={() => openModal('reward', 'edit', reward)}
                        >
                          <i className="zmdi zmdi-edit" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Delete"
                          onClick={() => handleDelete(reward)}
                        >
                          <i className="zmdi zmdi-delete" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && modalType === 'reward' && (
        <RewardFormModal
          initialPartners={initialPartners}
          initialCategories={initialCategories}
        />
      )}
    </div>
  );
};

export default RewardsTab;
